import { useEffect, useState } from "react";
import {
    fetchUnattributedOrders,
    saveAttributions,
    type Settings,
    type UnattributedOrder,
} from "../api";
import { CampaignPicker } from "./CampaignPicker";

interface CampaignOption {
    id: string;
    name: string;
}

interface UnattributedOrdersModalProps {
    settings: Settings;
    from: string;
    to: string;
    campaigns: CampaignOption[];
    onClose: () => void;
    onSaved: () => void;
}

export function UnattributedOrdersModal({
    settings,
    from,
    to,
    campaigns,
    onClose,
    onSaved,
}: UnattributedOrdersModalProps) {
    const [orders, setOrders] = useState<UnattributedOrder[]>([]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [pending, setPending] = useState<Map<string, string>>(new Map());
    const [bulkCampaignId, setBulkCampaignId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fmt = (n: number, currency: string) =>
        n.toLocaleString(undefined, { style: "currency", currency, maximumFractionDigits: 0 });

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchUnattributedOrders(settings, from, to)
            .then((r) => !cancelled && setOrders(r.items))
            .catch((e) => !cancelled && setError((e as Error).message))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [settings, from, to]);

    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (e.key === "Escape") tryClose();
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    });

    function tryClose() {
        if (pending.size > 0) {
            const ok = window.confirm(`Discard ${pending.size} unsaved changes?`);
            if (!ok) return;
        }
        onClose();
    }

    function toggleRow(id: string) {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    }

    function setRowCampaign(id: string, campaignId: string | null) {
        const next = new Map(pending);
        if (campaignId === null) next.delete(id);
        else next.set(id, campaignId);
        setPending(next);
    }

    function applyBulk() {
        if (!bulkCampaignId || selected.size === 0) return;
        const next = new Map(pending);
        for (const id of selected) next.set(id, bulkCampaignId);
        setPending(next);
        setSelected(new Set());
        setBulkCampaignId(null);
    }

    async function save() {
        if (pending.size === 0) return;
        setSaving(true);
        setError(null);
        // Group order IDs by campaign_id, one POST per group.
        const byCampaign = new Map<string, string[]>();
        for (const [orderId, campaignId] of pending) {
            const existing = byCampaign.get(campaignId);
            if (existing) existing.push(orderId);
            else byCampaign.set(campaignId, [orderId]);
        }
        try {
            for (const [campaignId, orderIds] of byCampaign) {
                await saveAttributions(settings, orderIds, campaignId);
            }
            onSaved();
            onClose();
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setSaving(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) tryClose();
            }}
        >
            <div className="bg-okiru-panel border border-okiru-muted/20 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
                <header className="px-6 py-4 border-b border-okiru-muted/15 flex items-start justify-between">
                    <div>
                        <h2 className="text-lg font-semibold">Unattributed orders</h2>
                        <p className="text-xs text-okiru-muted mt-1">
                            Meta-sourced orders without a campaign match. Pick campaigns and Save to update ROAS.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={tryClose}
                        className="text-okiru-muted hover:text-okiru-text text-xl leading-none px-2"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading ? (
                        <div className="text-okiru-muted text-sm">Loading…</div>
                    ) : error ? (
                        <div className="bg-red-950 border border-red-700 text-red-200 px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="text-okiru-muted text-sm py-8 text-center">
                            All Meta-sourced orders in this range have been attributed.
                        </div>
                    ) : (
                        <ul className="space-y-3">
                            {orders.map((o) => {
                                const pendingCampaign = pending.get(o.id) ?? null;
                                return (
                                    <li
                                        key={o.id}
                                        className="border border-okiru-muted/15 rounded-lg p-3 bg-okiru-bg/30"
                                    >
                                        <div className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                checked={selected.has(o.id)}
                                                onChange={() => toggleRow(o.id)}
                                                className="mt-1 accent-okiru-accent"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline gap-3 text-sm flex-wrap">
                                                    <span className="font-medium">{o.name}</span>
                                                    <span className="text-okiru-muted text-xs">
                                                        {o.processed_at.slice(0, 10)}
                                                    </span>
                                                    <span className="ml-auto whitespace-nowrap">
                                                        {fmt(o.total_price, o.currency)}
                                                    </span>
                                                </div>
                                                {o.landing_site && (
                                                    <div className="text-xs text-okiru-muted mt-1 truncate" title={o.landing_site}>
                                                        {o.landing_site}
                                                    </div>
                                                )}
                                                <div className="mt-2 flex items-center gap-2">
                                                    <span className="text-xs text-okiru-muted">Campaign:</span>
                                                    <CampaignPicker
                                                        campaigns={campaigns}
                                                        value={pendingCampaign}
                                                        onChange={(id) => setRowCampaign(o.id, id)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <footer className="px-6 py-3 border-t border-okiru-muted/15 flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-okiru-muted whitespace-nowrap">
                        {selected.size} selected
                    </span>
                    <span className="text-xs text-okiru-muted">·</span>
                    <span className="text-xs text-okiru-muted whitespace-nowrap">Assign all to:</span>
                    <CampaignPicker
                        campaigns={campaigns}
                        value={bulkCampaignId}
                        onChange={setBulkCampaignId}
                    />
                    <button
                        type="button"
                        onClick={applyBulk}
                        disabled={!bulkCampaignId || selected.size === 0}
                        className="text-xs px-3 py-1.5 rounded-md border border-okiru-muted/30 hover:border-okiru-accent disabled:opacity-40 disabled:cursor-not-allowed transition whitespace-nowrap"
                    >
                        Apply to selection
                    </button>
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            type="button"
                            onClick={tryClose}
                            className="text-xs px-3 py-1.5 rounded-md border border-okiru-muted/30 hover:border-red-400 hover:text-red-400 transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={save}
                            disabled={pending.size === 0 || saving}
                            className="text-xs px-3 py-1.5 rounded-md bg-okiru-accent text-okiru-bg font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
                        >
                            {saving ? "Saving…" : pending.size > 0 ? `Save ${pending.size} change${pending.size === 1 ? "" : "s"}` : "Save"}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
}
